import { memo, useMemo } from 'react';
import { Tabs } from 'react-aria-components';

import { ComponentStack, Tab, TabList, TabPanel } from '@/components/core';
import { ValidationPanelHeader } from '@/components/flow';
import { BetaBadge } from '@/components/ui';
import { toValidationId, useGetValidation } from '@/hooks/api';
import { Notification, useValidationConfigStore, ValidationConfigProvider } from '@/store';

import { NotificationSettings } from './NotificationSettings';
import { ReviewerSettings } from './ReviewerSettings';

const IValidationPanelTabs = () => {
  const selectedTab = useValidationConfigStore((state) => state.selectedTab);
  const setSelectedTab = useValidationConfigStore((state) => state.setSelectedTab);

  return (
    <Tabs
      className="group/model-panel"
      selectedKey={selectedTab}
      onSelectionChange={(key) => setSelectedTab(key.toString())}
    >
      <TabList>
        <Tab id="reviewers">Reviewers</Tab>
        <Tab id="notifications">
          Notifications
          <BetaBadge />
        </Tab>
      </TabList>

      <TabPanel id="reviewers">
        <ReviewerSettings />
      </TabPanel>

      <TabPanel id="notifications">
        <NotificationSettings />
      </TabPanel>
    </Tabs>
  );
};

const ValidationPanelTabs = memo(IValidationPanelTabs);

export type ValidationPanelProps = {
  actionId: string;
  agentId: string;
  validationId: string;
};

const IValidationPanel = ({ validationId }: ValidationPanelProps) => {
  const { data: validation } = useGetValidation(toValidationId(validationId));
  const notifications = useMemo(() => {
    const notifications = validation?.config?.notifications ?? {};
    try {
      return Object.entries(notifications).map(([id, n]) => ({ id, ...n })) as Notification[];
    } catch {
      /* Bad notification configuration */
      console.warn('Bad notification configuration', notifications);
      return [];
    }
  }, [validation]);

  return (
    <div className="flex w-full flex-col">
      <ValidationConfigProvider validationId={validationId} notifications={notifications}>
        <ValidationPanelHeader className="border-b border-gray-200 p-4" validationId={validationId} />
        <ComponentStack>
          <ValidationPanelTabs />
        </ComponentStack>
      </ValidationConfigProvider>
    </div>
  );
};

export const ValidationPanel = memo(IValidationPanel) as typeof IValidationPanel;
